import { Check, Copy } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { track } from "@/lib/analytics";
import { type ShareState, useSetShare } from "@/lib/api";
import { logger } from "@/lib/logger";

interface SharePanelProps {
  assessmentId: string;
  share: ShareState;
}

/**
 * Owner-only control for the report's public, view-only share link. The parent
 * renders this solely when `record.share` is present, which the backend
 * populates only for the report's owner, so the live token never reaches an
 * admin or any other viewer.
 */
export function SharePanel({ assessmentId, share }: SharePanelProps) {
  const setShare = useSetShare(assessmentId);
  const [copied, setCopied] = useState(false);

  const shareUrl = share.share_token
    ? `${window.location.origin}/report/shared/${share.share_token}`
    : "";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      // Clipboard access is denied on insecure origins and by some browsers.
      // The link stays selectable in the read-only input, so this is not fatal.
      logger.warn("[share] clipboard write failed", error);
    }
  };

  return (
    <Card className="mt-6">
      <CardContent className="pt-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <label htmlFor="share-toggle" className="text-primary text-sm font-bold">
              Public share link
            </label>
            <p className="text-muted-foreground mt-1 max-w-[60ch] text-sm">
              Anyone with the link sees the original system report, with no owner details or
              expert-calibration notes. Turning it off revokes the link for good; turning it back on
              creates a different one.
            </p>
          </div>
          <Switch
            id="share-toggle"
            checked={share.enabled}
            disabled={setShare.isPending}
            onCheckedChange={(next) =>
              setShare.mutate(next, {
                onSuccess: () => {
                  // Only enabling is a "share"; toggling the link off is not.
                  if (next) {
                    track("report_shared", { method: "link" });
                  }
                },
              })
            }
          />
        </div>

        {share.enabled && share.share_token && (
          <div className="mt-4 flex items-center gap-2">
            <Input readOnly value={shareUrl} aria-label="Public share link" />
            <Button variant="outline" className="shrink-0" onClick={() => void handleCopy()}>
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
        )}

        {setShare.isError && (
          <p className="text-destructive mt-3 text-sm">
            Couldn’t update sharing. Please try again.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
